import { useState, useRef } from 'react';
import logger from '@/utils/logger';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Camera, Sparkles, Save, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import Navbar from '@/components/Navbar';
import MultiImageUpload from '@/components/MultiImageUpload';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AddYarn() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    brand: '',
    color: '',
    weight: '',
    fiber_content: '',
    yardage: '',
    quantity: 1,
    purchase_date: '',
    location: '',
    price: '',
    dye_lot: '',
    hook_size: '',
    gauge: '',
    images: []
  });
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setScanning(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64Image = event.target.result.split(',')[1];
        
        try {
          const response = await axios.post(
            `${API}/yarns/extract-from-image`,
            {
              image_base64: base64Image,
              mime_type: file.type
            },
            { withCredentials: true }
          );

          const extracted = response.data;
          setFormData(prev => ({
            ...prev,
            brand: extracted.brand || prev.brand,
            color: extracted.color || prev.color,
            weight: extracted.weight || prev.weight,
            fiber_content: extracted.fiber_content || prev.fiber_content,
            yardage: extracted.yardage || prev.yardage,
            dye_lot: extracted.dye_lot || prev.dye_lot,
            hook_size: extracted.hook_size || prev.hook_size,
            gauge: extracted.gauge || prev.gauge
          }));

          toast.success('Yarn details extracted successfully!');
        } catch (error) {
          logger.error('Failed to extract yarn info:', error);
          toast.error('Failed to extract yarn information');
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      logger.error('Failed to read file:', error);
      toast.error('Failed to read image');
    } finally {
      setScanning(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const dataToSend = {
        ...formData,
        quantity: parseInt(formData.quantity) || 1,
        price: formData.price ? parseFloat(formData.price) : null
      };

      await axios.post(`${API}/yarns`, dataToSend, {
        withCredentials: true
      });

      toast.success(t('common.success'));
      navigate('/inventory');
    } catch (error) {
      logger.error('Failed to add yarn:', error);
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleImageUploadComplete = (images) => {
    setFormData(prev => ({ ...prev, images: images || [] }));
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button
          data-testid="back-button"
          variant="ghost"
          onClick={() => navigate('/inventory')}
          className="mb-6 rounded-lg text-stone-600 hover:text-primary hover:bg-rose-50 font-medium"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('addYarn.backToInventory')}
        </Button>

        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-extrabold tracking-tight mb-2">
            {t('addYarn.title')}
          </h1>
          <p className="text-lg text-muted-foreground font-body">
            {t('addYarn.subtitle')}
          </p>
        </div>

        <div className="rounded-3xl bg-white border border-stone-100 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] mb-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center h-24 w-24 rounded-full bg-gradient-to-br from-rose-100 to-orange-100 mb-4">
              <Camera className="h-12 w-12 text-primary" />
            </div>
            <h3 className="text-xl font-heading font-semibold mb-2">{t('addYarn.aiScanner.title')}</h3>
            <p className="text-muted-foreground font-body mb-6">
              {t('addYarn.aiScanner.description')}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleImageUpload}
              className="hidden"
            />
            <Button
              data-testid="scan-button"
              onClick={() => fileInputRef.current?.click()}
              disabled={scanning}
              className="rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5 transition-all duration-300 font-bold"
            >
              {scanning ? (
                <>
                  <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-solid border-white border-r-transparent"></div>
                  {t('addYarn.aiScanner.scanning')}
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  {t('addYarn.aiScanner.scanButton')}
                </>
              )}
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="rounded-3xl bg-white border border-stone-100 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <h3 className="text-2xl font-heading font-bold mb-6">{t('addYarn.details')}</h3>
          
          {/* Image Upload Section */}
          <div className="mb-8">
            <MultiImageUpload
              onImagesChange={handleImageUploadComplete}
              currentImages={formData.images}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="brand" className="text-sm font-medium mb-2 block">{t('addYarn.fields.brand')}</Label>
              <Input
                data-testid="input-brand"
                id="brand"
                name="brand"
                value={formData.brand}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="color" className="text-sm font-medium mb-2 block">{t('addYarn.fields.color')}</Label>
              <Input
                data-testid="input-color"
                id="color"
                name="color"
                value={formData.color}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="weight" className="text-sm font-medium mb-2 block">{t('addYarn.fields.weight')}</Label>
              <Input
                data-testid="input-weight"
                id="weight"
                name="weight"
                placeholder={t('addYarn.fields.weightPlaceholder')}
                value={formData.weight}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="fiber_content" className="text-sm font-medium mb-2 block">{t('addYarn.fields.fiber')}</Label>
              <Input
                data-testid="input-fiber"
                id="fiber_content"
                name="fiber_content"
                placeholder={t('addYarn.fields.fiberPlaceholder')}
                value={formData.fiber_content}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="yardage" className="text-sm font-medium mb-2 block">{t('addYarn.fields.yardage')}</Label>
              <Input
                data-testid="input-yardage"
                id="yardage"
                name="yardage"
                placeholder={t('addYarn.fields.yardagePlaceholder')}
                value={formData.yardage}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="quantity" className="text-sm font-medium mb-2 block">{t('addYarn.fields.quantity')}</Label>
              <Input
                data-testid="input-quantity"
                id="quantity"
                name="quantity"
                type="number"
                min="1"
                value={formData.quantity}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="hook_size" className="text-sm font-medium mb-2 block">{t('addYarn.fields.hookSize')}</Label>
              <Input
                data-testid="input-hook-size"
                id="hook_size"
                name="hook_size"
                placeholder={t('addYarn.fields.hookSizePlaceholder')}
                value={formData.hook_size}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="gauge" className="text-sm font-medium mb-2 block">{t('addYarn.fields.gauge')}</Label>
              <Input
                data-testid="input-gauge"
                id="gauge"
                name="gauge"
                placeholder={t('addYarn.fields.gaugePlaceholder')}
                value={formData.gauge}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="dye_lot" className="text-sm font-medium mb-2 block">{t('addYarn.fields.dyeLot')}</Label>
              <Input
                data-testid="input-dye-lot"
                id="dye_lot"
                name="dye_lot"
                value={formData.dye_lot}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="purchase_date" className="text-sm font-medium mb-2 block">{t('addYarn.fields.purchaseDate')}</Label>
              <Input
                data-testid="input-purchase-date"
                id="purchase_date"
                name="purchase_date"
                type="date"
                value={formData.purchase_date}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="location" className="text-sm font-medium mb-2 block">{t('addYarn.fields.location')}</Label>
              <Input
                data-testid="input-location"
                id="location"
                name="location"
                placeholder={t('addYarn.fields.locationPlaceholder')}
                value={formData.location}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="price" className="text-sm font-medium mb-2 block">{t('addYarn.fields.price')}</Label>
              <Input
                data-testid="input-price"
                id="price"
                name="price"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={formData.price}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>
          </div>

          <div className="mt-8 flex justify-end space-x-4">
            <Button
              data-testid="cancel-button"
              type="button"
              variant="ghost"
              onClick={() => navigate('/inventory')}
              className="rounded-lg text-stone-600 hover:text-primary hover:bg-rose-50 font-medium"
            >
              {t('common.cancel')}
            </Button>
            <Button
              data-testid="save-yarn-button"
              type="submit"
              disabled={saving}
              className="rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5 transition-all duration-300 font-bold"
            >
              {saving ? (
                <>
                  <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-solid border-white border-r-transparent"></div>
                  {t('addYarn.saving')}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {t('addYarn.saveYarn')}
                </>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
